/**
 * Error Filter Utility
 * Filters out browser extension errors and other non-application errors from console
 */

// Store original console methods at module load time (before any other overrides)
// These will be the browser's native console methods
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

// Store a reference to check if console methods have been overridden
let consoleMethodsStored = true;

// Type for error patterns supporting both strings and regex
type ErrorPattern = string | RegExp;

// Common browser extension error patterns
const extensionErrorPatterns: ErrorPattern[] = [
  'runtime.lastError',
  'background.js',
  'FrameDoesNotExistError',
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
  'The message port closed before a response was received',
  'The page keeping the extension port is moved into back/forward cache',
  'Frame 0 does not exist in tab',
  'ERR_FILE_NOT_FOUND',
  'completion_list.html',
  'utils.js:1 Failed to load resource',
  'utils.js',
  'extensionState.js',
  'heuristicsRedefinitions.js',
  'DelayedMessageSender',
  'Extension context invalidated',
  'Receiving end does not exist',
  'Could not establish connection',
  'Could not establish connection. Receiving end does not exist',
  'Maximum call stack size exceeded',
  'RangeError',
  'web_monetization_wall',
  'web_monetization_wall_executable',
  'Error: no_div',
  'adsbygoogle',
  'Cannot read properties of null',
  'ControlLooksLikePasswordCredentialField',
  'Unchecked runtime.lastError',
  'Unchecked runtime.lastError:',
  'content_script.js',
  'shouldOfferCompletionListForField',
  'elementWasFocused',
  'focusInEventHandler',
  'processInputEvent',
  // Regex patterns for more flexible matching
  /chrome-extension:\/\/[a-z0-9]+\//i,
  /moz-extension:\/\/[a-z0-9]+\//i,
  /safari-extension:\/\/[a-z0-9]+\//i,
  /background\.js:\d+/i,
  /content_script\.js:\d+/i,
  /Frame \d+ does not exist in tab \d+/i,
  /Cannot read properties of undefined.*reading 'control'/i,
  /Unchecked runtime\.lastError:.*message port/i,
  /Unchecked runtime\.lastError:.*Could not establish connection/i,
  /Uncaught.*in promise.*FrameDoesNotExistError/i,
  /utils\.js.*Failed to load resource/i,
  /extensionState\.js.*Failed to load resource/i,
  /heuristicsRedefinitions\.js.*Failed to load resource/i
];

// Common network error patterns that are usually not application errors
const networkErrorPatterns: ErrorPattern[] = [
  'net::ERR_FILE_NOT_FOUND',
  'net::ERR_NAME_NOT_RESOLVED',
  'net::ERR_CONNECTION_REFUSED',
  'net::ERR_ABORTED',
  'net::ERR_TIMED_OUT',
  'net::ERR_INTERNET_DISCONNECTED',
  'net::ERR_NETWORK_CHANGED',
  'net::ERR_CONNECTION_RESET',
  'net::ERR_CONNECTION_CLOSED',
  'net::ERR_CONNECTION_TIMED_OUT',
  'net::ERR_CONNECTION_ABORTED',
  // Regex patterns for network errors
  /net::ERR_\w+/i
];

// Common third-party script error patterns
const thirdPartyErrorPatterns: ErrorPattern[] = [
  'google-analytics.com',
  'doubleclick.net',
  'googleads.g.doubleclick.net',
  'googleadservices.com',
  'criteo.com',
  'dis.criteo.com',
  'facebook.com',
  'connect.facebook.net',
  'twitter.com',
  'platform.twitter.com',
  'linkedin.com',
  'platform.linkedin.com',
  // Google Ads / pagead 404s (third-party; we cannot fix these)
  'zrt_lookup.html',
  'zrt_lookup',
  'pagead/html',
  'pagead2.googlesyndication',
  // Cloudflare Zaraz (ST-2026-155)
  'cdn-cgi/zaraz',
  '/cdn-cgi/zaraz/',
  'zaraz/s.js',
  // Tracking prevention warnings
  'Tracking Prevention blocked access to storage',
  'tracking prevention blocked',
  // Regex patterns for third-party domains
  /\.(google|facebook|twitter|linkedin|criteo)\.(com|net|org|io|co)/i,
  /doubleclick|googleadservices|criteo/i,
  /tracking prevention.*blocked.*storage/i,
  /cdn-cgi\/zaraz/i
];

// Emotion and React multiple instance warning patterns
const emotionReactPatterns: ErrorPattern[] = [
  'You are loading @emotion/react when it is already loaded',
  'Running multiple instances may cause problems',
  'This can happen if multiple versions are used',
  'or if multiple builds of the same version are used',
  // Don't suppress these critical React errors as they indicate real issues
  // 'Invalid hook call',
  // 'Hooks can only be called inside of the body of a function component',
  // 'You might have mismatching versions of React and the renderer',
  // 'You might be breaking the Rules of Hooks',
  // 'You might have more than one copy of React in the same app',
  // 'Cannot read properties of null (reading \'useState\')',
  // More specific patterns for Emotion warnings
  'emotion-react.browser.development.esm.js',
  'emotion-styled-base.browser.development.esm.js',
  'emotion-styled.browser.development.esm.js',
  // Regex patterns for Emotion/React warnings
  /@emotion\/react.*already loaded/i,
  /multiple instances may cause problems/i,
  // Don't suppress these critical React errors
  // /Invalid hook call/i,
  // /Hooks can only be called inside/i,
  // /Cannot read properties of null.*useState/i,
  /emotion-react\.browser\.development\.esm\.js/i,
  /emotion-styled.*\.browser\.development\.esm\.js/i
];

// Twilio Conversations error patterns (expected in development when not configured)
const twilioErrorPatterns: ErrorPattern[] = [
  'Twilio client failed to initialize',
  'Twilio Conversations is not available',
  'Client initialization failed',
  'Twilio Conversations not configured',
  'onTokenRejected',
  'token rejected',
  'Conversation Service SID',
  'Twilio Conversations will be disabled',
  'This is likely a Twilio configuration issue',
  // Regex patterns for Twilio errors
  /Twilio.*failed to initialize/i,
  /Twilio.*not available/i,
  /Twilio.*not configured/i,
  /Client initialization failed/i,
  /token.*rejected/i,
  /Conversation Service SID/i
];

// Application-specific patterns that should NOT be filtered
const applicationPatterns: ErrorPattern[] = [
  'Homepage:',
  'Quick fallback triggered',
  'Data loading timeout',
  'API failed',
  'using fallback'
];

// Combine all error patterns and deduplicate using Set
const allErrorPatterns: ErrorPattern[] = Array.from(new Set([
  ...extensionErrorPatterns,
  ...networkErrorPatterns,
  ...thirdPartyErrorPatterns,
  ...emotionReactPatterns,
  // Only filter Twilio errors in development mode when not configured
  ...(import.meta.env.DEV ? twilioErrorPatterns : []),
]));

/**
 * Check if an error message should be filtered out
 * Now supports both string and regex patterns, and scans all console arguments
 */
export const shouldFilterError = (message: string): boolean => {
  if (!message || typeof message !== 'string') {
    return false;
  }
  
  // First check if this is an application message that should NOT be filtered
  const isApplicationMessage = applicationPatterns.some(pattern => {
    if (pattern instanceof RegExp) {
      return pattern.test(message);
    } else {
      return message.toLowerCase().includes(pattern.toLowerCase());
    }
  });
  
  // If it's an application message, don't filter it
  if (isApplicationMessage) {
    return false;
  }
  
  // Otherwise, check against filter patterns
  return allErrorPatterns.some(pattern => {
    if (pattern instanceof RegExp) {
      return pattern.test(message);
    } else {
      return message.toLowerCase().includes(pattern.toLowerCase());
    }
  });
};

/**
 * Check if console arguments should be filtered out
 * Scans all arguments for error patterns, not just the first one
 */
export const shouldFilterConsoleArgs = (args: any[]): boolean => {
  if (!args || args.length === 0) {
    return false;
  }
  
  // Convert all arguments to strings and join them, including stack traces
  const message = args.map(arg => {
    if (typeof arg === 'string') {
      return arg;
    } else if (arg && typeof arg.toString === 'function') {
      const str = arg.toString();
      // Also check stack trace if it's an Error object
      if (arg instanceof Error && arg.stack) {
        return str + ' ' + arg.stack;
      }
      return str;
    } else if (arg && typeof arg === 'object') {
      // Handle error objects and other objects
      let result = '';
      if (arg.message) {
        result += arg.message;
      } else if (arg.error && arg.error.message) {
        result += arg.error.message;
      }
      // Include stack trace if available
      if (arg.stack) {
        result += ' ' + arg.stack;
      } else if (arg.error && arg.error.stack) {
        result += ' ' + arg.error.stack;
      }
      if (!result) {
        result = JSON.stringify(arg);
      }
      return result;
    }
    return '';
  }).join(' ');
  
  return shouldFilterError(message);
};

// Guard to prevent infinite recursion in error handlers
let isProcessingError = false;
const MAX_ERROR_DEPTH = 5;
let errorDepth = 0;

/**
 * Initialize error filtering for console methods
 */
export const initializeErrorFilter = (): void => {
  // Get the current console methods (which may have been overridden by main.tsx)
  // We need to chain through any existing overrides to preserve the call chain
  const currentConsoleError = console.error;
  const currentConsoleWarn = console.warn;
  
  // Determine if we should chain through existing overrides
  // If console methods have been overridden, we need to call the current override,
  // not the original browser method, to preserve the chain
  const shouldChainError = currentConsoleError !== originalConsoleError;
  const shouldChainWarn = currentConsoleWarn !== originalConsoleWarn;
  
  // Override console.error with improved argument scanning
  // Chain through existing override if present to preserve call chain
  console.error = (...args: any[]) => {
    // Prevent infinite recursion
    if (isProcessingError || errorDepth >= MAX_ERROR_DEPTH) {
      return;
    }
    
    try {
      errorDepth++;
      isProcessingError = true;
      
      // Filter out "Maximum call stack size exceeded" errors from extensions
      const message = args.map(arg => String(arg)).join(' ');
      if (message.includes('Maximum call stack size exceeded') || 
          message.includes('web_monetization_wall') ||
          message.includes('RangeError') ||
          message.includes('FocusTrap.js') ||
          message.includes('contain @ FocusTrap')) {
        return; // Suppress stack overflow errors
      }
      
      if (!shouldFilterConsoleArgs(args)) {
        // Chain through existing override if present, otherwise use original browser method
        if (shouldChainError) {
          currentConsoleError.apply(console, args);
        } else {
          originalConsoleError.apply(console, args);
        }
      }
    } finally {
      errorDepth--;
      if (errorDepth <= 0) {
        isProcessingError = false;
        errorDepth = 0;
      }
    }
  };
  
  // Override console.warn with improved argument scanning
  // Chain through existing override if present to preserve call chain
  console.warn = (...args: any[]) => {
    // Prevent infinite recursion
    if (isProcessingError || errorDepth >= MAX_ERROR_DEPTH) {
      return;
    }
    
    try {
      errorDepth++;
      isProcessingError = true;
      
      if (!shouldFilterConsoleArgs(args)) {
        // Chain through existing override if present, otherwise use original browser method
        if (shouldChainWarn) {
          currentConsoleWarn.apply(console, args);
        } else {
          originalConsoleWarn.apply(console, args);
        }
      }
    } finally {
      errorDepth--;
      if (errorDepth <= 0) {
        isProcessingError = false;
        errorDepth = 0;
      }
    }
  };
  
  // Override window.onerror to filter extension errors
  const originalOnError = window.onerror;
  let isProcessingWindowError = false;
  window.onerror = (message, source, lineno, colno, error) => {
    // Prevent infinite recursion
    if (isProcessingWindowError) {
      return false;
    }
    
    try {
      isProcessingWindowError = true;
      const errorMessage = message?.toString() || '';
      const sourceFile = source?.toString() || '';
      
      // Immediately filter out stack overflow errors
      if (errorMessage.includes('Maximum call stack size exceeded') ||
          errorMessage.includes('RangeError') ||
          errorMessage.includes('web_monetization_wall')) {
        return false;
      }
      
      // Check both message and source file for extension errors
      const shouldFilter = shouldFilterError(errorMessage) || 
                          shouldFilterError(sourceFile) ||
                          sourceFile.includes('content_script.js') ||
                          sourceFile.includes('chrome-extension://') ||
                          sourceFile.includes('moz-extension://') ||
                          sourceFile.includes('safari-extension://');
      
      if (!shouldFilter) {
        if (originalOnError) {
          return originalOnError.call(window, message, source, lineno, colno, error);
        }
        // Let browser handle the error if no original handler
        return true;
      }
      
      // Only suppress if we're filtering the error
      return false;
    } finally {
      isProcessingWindowError = false;
    }
  };
  
  // Override window.onunhandledrejection to filter extension errors
  const originalOnUnhandledRejection = window.onunhandledrejection;
  let isProcessingRejection = false;
  window.onunhandledrejection = (event) => {
    // Prevent infinite recursion
    if (isProcessingRejection) {
      return;
    }
    
    try {
      isProcessingRejection = true;
      const reason = event.reason;
      const message = reason?.toString() || '';
      const errorStack = reason?.stack?.toString() || '';
      const errorName = reason?.name || '';
      const errorMessage = reason?.message || '';
      
      // Combine all error information for comprehensive checking
      const fullErrorText = [message, errorStack, errorName, errorMessage]
        .filter(Boolean)
        .join(' ');
      
      // Immediately filter out stack overflow errors
      if (message.includes('Maximum call stack size exceeded') ||
          message.includes('RangeError') ||
          message.includes('web_monetization_wall') ||
          message.includes('FocusTrap.js') ||
          message.includes('contain @ FocusTrap') ||
          errorName === 'RangeError') {
        event.preventDefault();
        return;
      }
      
      // Check all error information for extension errors
      const shouldFilter = shouldFilterError(fullErrorText) || 
                          shouldFilterError(message) || 
                          shouldFilterError(errorStack) ||
                          shouldFilterError(errorName) ||
                          shouldFilterError(errorMessage) ||
                          errorStack.includes('content_script.js') ||
                          errorStack.includes('background.js') ||
                          errorStack.includes('chrome-extension://') ||
                          errorStack.includes('moz-extension://') ||
                          errorStack.includes('safari-extension://') ||
                          errorName === 'FrameDoesNotExistError' ||
                          message.includes('FrameDoesNotExistError') ||
                          errorMessage.includes('FrameDoesNotExistError');
      
      if (!shouldFilter) {
        if (originalOnUnhandledRejection) {
          return originalOnUnhandledRejection.call(window, event);
        }
        // Let browser handle the rejection if no original handler
        return;
      }
      
      // Only suppress if we're filtering the error
      event.preventDefault();
    } finally {
      isProcessingRejection = false;
    }
  };
};

/**
 * Restore original console methods
 */
export const restoreConsoleMethods = (): void => {
  console.error = originalConsoleError;
  console.warn = originalConsoleWarn;
};

/**
 * Add custom error pattern to filter
 */
export const addErrorPattern = (pattern: string): void => {
  if (!allErrorPatterns.includes(pattern)) {
    allErrorPatterns.push(pattern);
  }
};

/**
 * Remove custom error pattern from filter
 */
export const removeErrorPattern = (pattern: string): void => {
  const index = allErrorPatterns.indexOf(pattern);
  if (index > -1) {
    allErrorPatterns.splice(index, 1);
  }
};

/**
 * Get all current error patterns
 */
export const getErrorPatterns = (): ErrorPattern[] => {
  return [...allErrorPatterns];
};

/**
 * Clear all error patterns
 */
export const clearErrorPatterns = (): void => {
  allErrorPatterns.length = 0;
};

// Auto-initialize error filtering (skip in test environment)
if (import.meta.env.MODE !== 'test') {
  initializeErrorFilter();
}
